import { Vue, prop } from 'vue-class-component'
import { markRaw } from 'vue'
import { debounce, observeResizeEvent, triggerDownload, ZoomAdapter } from '@/services/file-preview'

// Prevent error logging from PDFjs
/**
 * We proxy console.error to prevent PDF.js from directly writing errors to the console.
 * ideally, this would be handled during bundling by dropping console logs,
 * but since PDF.js is loaded via CDN, this workaround is the only feasible solution for now.
 */
(window as any)._console_error_ref = null;
(window as any)._console_error_proxy = (...args: any[]) => {
  args.forEach(arg => {
    (window as any)._pushError(arg)
  })
};
(window as any)._pushError = (error: any) => {
  if (!(window as any)._cachedErrors) {
    (window as any)._cachedErrors = []
  }
  (window as any)._cachedErrors.push(error)
}

const REGEX_NON_NUMBERS = /[^0-9]/g
const CUSTOM_RESIZE_EVENT_NAME = 'CUSTOM_RESIZE'
const CUSTOM_ZOOM_EVENT_NAME = 'CUSTOM_ZOOM'
const CUSTOM_RESIZE_DELAY = 300
const INPUT_CHANGE_DELAY = 800
const MIN_ZOOM_LEVEL = 10
const MAX_ZOOM_LEVEL = 400
const DOWNLOAD_IN_PROGRESS_DELAY = 1200

class Props {
  file = prop({
    type: Object,
    required: false,
    default: () => ({
      url: '',
      name: ''
    })
  });
}

export default class PdfPreview extends Vue.with(Props) {
  currentPage = 1;
  zoomLevel = 100;
  enableFitWidth = true;
  showLoader = false;
  totalPage = 0;
  pdfViewer: any | null = null;
  pdfEvents: any | null = null;
  container: HTMLDivElement | null = null;
  viewer: HTMLDivElement | null = null;
  pdfFile: any | null = null;
  pdfLinkService: any | null = null;
  viewPort: any | null = null;
  resizeObserver: any | null = null;
  zoomAdapter: ZoomAdapter | null = null;
  downloadInProgress = false;

  debouncedScaleChange = debounce((event: any) => {
    let formattedValue = parseInt((event.target?.value + '').replace(REGEX_NON_NUMBERS, ''), 10) || 0
    formattedValue = Math.min(Math.max(formattedValue, MIN_ZOOM_LEVEL), MAX_ZOOM_LEVEL)
    this.zoomLevel = formattedValue
    this.enableFitWidth = false
    this.scaleUpdate(this.zoomLevel)
  }, INPUT_CHANGE_DELAY);

  debouncedApplyPageChange = debounce((event: any) => {
    let formattedValue = parseInt((event.target?.value + '').replace(REGEX_NON_NUMBERS, ''), 10) || 0
    formattedValue = Math.min(Math.max(formattedValue, 1), this.totalPage)
    this.currentPage = formattedValue
    if (this.pdfViewer) {
      this.pdfViewer.currentPageNumber = this.currentPage
    }
  }, INPUT_CHANGE_DELAY);

  mounted () {
    (window as any)._console_error_ref = window.console.error
    window.console.error = (window as any)._console_error_proxy

    this.container = this.$refs.pageContainer as HTMLDivElement
    this.viewer = this.$refs.viewer as HTMLDivElement
    this.zoomAdapter = new ZoomAdapter(this.container, CUSTOM_ZOOM_EVENT_NAME)
    this.resizeObserver = observeResizeEvent(this.container, CUSTOM_RESIZE_EVENT_NAME, CUSTOM_RESIZE_DELAY)

    if (!window.pdfjsViewer || !window.pdfjsLib) {
      this.previewErrorHandler(new Error('PDF.js viewer scripts not loaded'))
      return
    }

    if (!this.container || !this.viewer) {
      this.previewErrorHandler(new Error('PDF container not ready'))
      return
    }

    this.pdfEvents = markRaw(new window.pdfjsViewer.EventBus())
    this.pdfLinkService = markRaw(new window.pdfjsViewer.PDFLinkService({
      eventBus: this.pdfEvents
    }))
    try {
      this.pdfViewer = markRaw(new window.pdfjsViewer.PDFViewer({
        container: this.container,
        viewer: this.viewer,
        eventBus: this.pdfEvents,
        linkService: this.pdfLinkService
      }))
      this.pdfLinkService.setViewer(this.pdfViewer)
      this.container.addEventListener(CUSTOM_RESIZE_EVENT_NAME, this.resizeEventListener)
      this.container.addEventListener(CUSTOM_ZOOM_EVENT_NAME, this.zoomEventHandler)
      this.pdfEvents.on('pagechanging', this.pageChangeEvent, true)
      this.pdfEvents.on('documentloaded', () => {
        this.pdfViewer.currentScale = 1
        this.fitAutoScale()
      })
    } catch (error) {
      (window as any)._pushError(error)
    }

    try {
      this.loadPdf()
    } catch (error) {
      this.previewErrorHandler(error)
    }
  }

  toggleFitWidth () {
    this.enableFitWidth = !this.enableFitWidth
    this.fitAutoScale()
  }

  previewErrorHandler (error: any) {
    this.$emit('error', error)
  }

  pageChangeEvent (evt: any) {
    this.currentPage = evt.pageNumber
  }

  downloadFile () {
    if (this.downloadInProgress) {
      return null
    }
    this.downloadInProgress = true
    triggerDownload(this.file.url, this.file.name)
    setTimeout(() => {
      this.downloadInProgress = false
    }, DOWNLOAD_IN_PROGRESS_DELAY)
  }

  beforeDestroy () {
    window.console.error = (window as any)._console_error_ref
    this.pdfEvents?.off('pagechanging', this.pageChangeEvent, true)
    if (this.container) {
      this.resizeObserver?.unobserve(this.container)
    }
    if (this.zoomAdapter) {
      this.zoomAdapter.unsubscribe()
    }
    this.container?.removeEventListener(CUSTOM_RESIZE_EVENT_NAME, this.resizeEventListener)
  }

  async loadPdf () {
    if (this.file.url && this.pdfViewer) {
      this.showLoader = true
      const loadingTask = window.pdfjsLib.getDocument({ url: this.file.url, withCredentials: true })
      try {
        const doc = await loadingTask.promise
        // Avoid Vue making this reactive; private fields in PDF.js objects must not be proxied
        this.pdfFile = markRaw(doc)
        this.showLoader = false
      } catch (e: any) {
        this.showLoader = false
        this.previewErrorHandler(e)
      }
      if (!this.pdfFile) {
        return
      }
      this.pdfViewer?.setDocument(this.pdfFile)
      this.pdfLinkService?.setDocument(this.pdfFile)
      const page = await this.pdfFile.getPage(1)
      this.totalPage = this.pdfViewer?.pagesCount || this.pdfViewer?.pdfDocument?._pdfInfo?.numPages || 0
      this.viewPort = page.getViewport({ scale: 1 })
    }
  }

  resizeEventListener () {
    this.fitAutoScale()
  }

  zoomEventHandler (event: Event) {
    this.enableFitWidth = false
    const zoomFactor = (event as CustomEvent).detail.zoomFactor || 1
    this.scaleUpdate(this.zoomLevel * zoomFactor)
  }

  fitAutoScale () {
    if (this.enableFitWidth) {
      const pageMarginSpace = 100
      const scaleFactor = 1.34
      let pageWidth = this.container?.offsetWidth || 1
      const viewportWidth = this.viewPort?.width || 1
      pageWidth = Math.max(pageWidth - pageMarginSpace, 1)
      let newScale = pageWidth / (viewportWidth * scaleFactor)
      newScale = Math.floor(newScale * 100)
      this.scaleUpdate(newScale)
    }
  }

  adjustZoom (value: number) {
    if (this.pdfViewer) {
      this.enableFitWidth = false
      const zoomValue = (this.pdfViewer.currentScale * 100) + value
      this.scaleUpdate(zoomValue)
    }
  }

  scaleUpdate (scaleValue: number | string) {
    let parsedInput = parseInt(scaleValue + '', 10)
    if (!Number.isInteger(parsedInput)) {
      parsedInput = MIN_ZOOM_LEVEL
    } else if (parsedInput < MIN_ZOOM_LEVEL) {
      parsedInput = MIN_ZOOM_LEVEL
    } else if (parsedInput > MAX_ZOOM_LEVEL) {
      parsedInput = MAX_ZOOM_LEVEL
    }
    if (this.pdfViewer) {
      this.zoomLevel = parsedInput
      this.pdfViewer.currentScale = parsedInput / 100
    }
  }
}
